import React, { useState, useEffect, useContext } from 'react';
import { useParams } from 'react-router-dom';
import { GenericServiceDeskPage } from '../../components/service-desk/GenericServiceDeskPage';
import { serviceDeskApi } from '../../utils/service-desk-api';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { AbilityContext } from '../../context/casl/AbilityContext';
import { ACTIONS, RESOURCES } from '../../constants/permissions.constants';

export default function DynamicServiceDeskPage() {
  const { deskId } = useParams<{ deskId: string }>();
  const [isLoading, setIsLoading] = useState(true);
  const [desk, setDesk] = useState<any>(null);
  const [tickets, setTickets] = useState<any[]>([]);
  const [error, setError] = useState<{ status: number, message: string } | null>(null);
  const { currentUser } = useAuth();
  const ability = useContext(AbilityContext);

  useEffect(() => {
    if (deskId) {
      loadData();
    }
  }, [deskId]);

  const loadData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [deskRes, ticketsRes] = await Promise.all([
        serviceDeskApi.getServiceDesk(deskId!),
        serviceDeskApi.getServiceDeskTickets(deskId!)
      ]);

      if (deskRes.success) {
        setDesk(deskRes.desk);
      } else {
          setError({ 
            status: deskRes.status || (deskRes.message?.includes('permission') ? 403 : 404), 
            message: deskRes.message || "Failed to load service desk." 
          });
      }
      
      if (ticketsRes.success) {
        setTickets(ticketsRes.tickets);
      }
    } catch (error: any) {
      console.error("Error loading service desk data:", error);
      // api.get/api.post throw ApiError with the HTTP status on non-OK responses;
      // preserve it so a 403 renders the Access Denied view instead of "Not Found".
      const status = error.status || 500;
      setError({
        status,
        message: status === 403
          ? "You do not have permission to access this service desk's internal management page."
          : "An unexpected error occurred."
      });
      if (status !== 403) {
        toast.error("Failed to load service desk data.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-4">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        <p className="text-sm text-muted-foreground animate-pulse">Loading service desk...</p>
      </div>
    );
  }

  if (error?.status === 403) {
      return (
          <div className="flex flex-col items-center justify-center py-20 px-6 text-center space-y-4">
              <div className="p-4 bg-amber-50 rounded-full">
                  <svg className="w-12 h-12 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Access Denied</h2>
              <p className="text-muted-foreground max-w-md mx-auto">
                  {error.message || "You do not have permission to access this service desk's internal management page."}
              </p>
              <button 
                  onClick={() => window.history.back()}
                  className="mt-4 px-6 py-2 bg-primary text-white font-bold rounded-lg shadow-md hover:bg-primary/90 transition-all"
              >
                  Go Back
              </button>
          </div>
      );
  }

  if (!desk) {
    return (
      <div className="text-center py-20">
        <h2 className="text-2xl font-bold text-gray-900">Service Desk Not Found</h2>
        <p className="text-muted-foreground mt-2">{error?.message || "The service desk you are looking for does not exist."}</p>
      </div>
    );
  }

  // Map backend tickets to frontend Ticket model
  const mappedTickets = tickets.map(t => {
    const createdAt = new Date(t.createdAt);
    const now = new Date();
    const diffHours = (now.getTime() - createdAt.getTime()) / (1000 * 60 * 60);
    const slaHours = desk.slaHours || 48;
    const isBreached = diffHours > slaHours && !['RESOLVED', 'CLOSED'].includes(t.status);

    const getInitials = (name: string) => name?.split(' ').map((n: string) => n[0]).join('').toUpperCase() || '?';
    const colors = ['bg-blue-500', 'bg-green-500', 'bg-purple-500', 'bg-pink-500', 'bg-orange-500', 'bg-indigo-500'];
    const getUserColor = (userId: any) => colors[parseInt(userId) % colors.length] || 'bg-gray-500';

    return {
      id: t.id.toString(),
      formattedId: `REQ-${t.id.toString().padStart(3, '0')}`,
      title: t.title,
      category: t.formVersion?.requestForm?.name || 'General',
      serviceDesk: desk.name,
      status: t.status,
      priority: (t.priority.charAt(0).toUpperCase() + t.priority.slice(1).toLowerCase()) as any,
      submitter: {
        name: t.requester?.name || 'Unknown',
        initials: getInitials(t.requester?.name),
        email: t.requester?.email || '',
        color: getUserColor(t.requester?.id)
      },
      assignees: (t.assignees || []).map((u: any) => ({
        name: u.name,
        initials: getInitials(u.name),
        email: u.email,
        color: getUserColor(u.id)
      })),
      createdAt: t.createdAt,
      isBreached,
      comments: (t.comments || []).filter((c: any) => !c.isPrivate).length,
      attachments: t.attachments?.length || 0,
      tags: t.data?.tags || []
    };
  });

  // Map forms (Exclude retired)
  const mappedForms = (desk.forms || [])
    .filter((f: any) => !f.isRetired)
    .map((f: any) => ({
        id: f.id.toString(),
        name: f.name,
        description: f.description || '',
        version: f.versions?.[0]?.versionNumber || 1,
        status: f.versions?.find((v: any) => v.status === 'PUBLISHED') ? 'PUBLISHED' : (f.versions?.[0]?.status || 'DRAFT')
    }));

  const isAuthorizedByPermission = ability.can(ACTIONS.MANAGE, RESOURCES.SERVICE_DESK_REQUESTER) || 
                                   ability.can(ACTIONS.MANAGE, RESOURCES.SERVICE_DESK_ADMIN) || 
                                   ability.can(ACTIONS.MANAGE, RESOURCES.SERVICE_DESK_TEAM_ADMIN);

  const isAuthorizedBySelection = desk.submissionControl === 'open' || 
                                  desk.authorizedRequesters?.some((u: any) => String(u.id) === String(currentUser?.id));

  const canSubmit = isAuthorizedByPermission && isAuthorizedBySelection && !desk.isRetired;

  return (
    <GenericServiceDeskPage 
      id={desk.id.toString()}
      title={desk.name}
      description={desk.description}
      initialTickets={mappedTickets}
      forms={mappedForms}
      statsLabel={`Total ${desk.name} Requests`}
      searchPlaceholder={`Search ${desk.name.toLowerCase()}...`}
      isRetired={desk.isRetired}
      canSubmit={canSubmit}
    />
  );
}
